import { z } from "zod";

/** Wire format (JSON API); maps to Prisma enums on the server. */
export const exitSurveyScenarioSchema = z.enum(["CART_ABANDON", "BROWSE_ABANDON"]);
export type ExitSurveyScenarioWire = z.infer<typeof exitSurveyScenarioSchema>;

export const cartPrimaryReasonSchema = z.enum([
    "browsing_no_plan_to_buy",
    "more_questions",
    "not_comfortable_without_visit",
    "something_else",
]);

export const browseVisitDescriptionSchema = z.enum([
    "just_browsing",
    "gathering_info_before_call_or_visit",
    "researching_pre_arranged",
    "hoped_to_purchase_couldnt_figure_out",
    "something_else",
]);

export const contactPreferenceSchema = z.enum(["text", "email", "phone", "none"]);

const cartAnswersSchema = z.object({
    primaryReason: cartPrimaryReasonSchema,
    primaryReasonOther: z.string().max(2000).optional(),
});

const browseAnswersSchema = z.object({
    visitDescription: browseVisitDescriptionSchema,
    visitDescriptionOther: z.string().max(2000).optional(),
    siteUnansweredQuestions: z.enum(["yes", "no"]),
    siteUnansweredDetail: z.string().max(2000).optional(),
});

const exitSurveyTriggerSchema = z.enum(["exit_intent", "idle", "scroll_depth", "leave_cart"]);

export const exitSurveyPayloadSchema = z
    .discriminatedUnion("scenario", [
        z.object({
            scenario: z.literal("CART_ABANDON"),
            answers: cartAnswersSchema,
            contactPreference: contactPreferenceSchema,
            contactName: z.string().max(200).optional(),
            contactValue: z.string().max(500).optional(),
            pageUrl: z.string().max(2000).optional(),
            trigger: exitSurveyTriggerSchema.optional(),
        }),
        z.object({
            scenario: z.literal("BROWSE_ABANDON"),
            answers: browseAnswersSchema,
            contactPreference: contactPreferenceSchema,
            contactName: z.string().max(200).optional(),
            contactValue: z.string().max(500).optional(),
            pageUrl: z.string().max(2000).optional(),
            trigger: exitSurveyTriggerSchema.optional(),
        }),
    ])
    .superRefine((data, ctx) => {
        if (data.contactPreference !== "none") {
            const v = data.contactValue?.trim();
            if (!v) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: "Contact detail is required for the selected contact method",
                    path: ["contactValue"],
                });
            }
            const n = data.contactName?.trim();
            if (!n) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: "Name is required for the selected contact method",
                    path: ["contactName"],
                });
            }
        }
    });

export type ExitSurveyPayload = z.infer<typeof exitSurveyPayloadSchema>;

export function isExitSurveyEnabledClient() {
    return import.meta.env.VITE_EXIT_SURVEY_ENABLED !== "false";
}

export function isExitSurveyEnabledServer() {
    return process.env.VITE_EXIT_SURVEY_ENABLED !== "false";
}
